import { useRef } from 'react';

import type { Types } from '@cornerstonejs/core';
// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import { utilities } from '@cornerstonejs/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { uuidv4 } from '@cornerstonejs/core/dist/esm/utilities';
import { PlanarFreehandROITool, annotation } from '@cornerstonejs/tools';
import type { Annotation } from '@cornerstonejs/tools/dist/esm/types';
import { differenceInMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';

import { selectedVisitAtom } from '../../app/appHeader/imageViewerMenu/atoms';
import { usePreferences } from '../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../mixpanel';

export const buildCornerstoneFriendlyAnnotation = ({
  className,
  pointsArray,
  toolName,
  viewportForImage,
  viewportId,
  dicomCanvasRef,
  color,
  renderDashedLine,
  isLocked = false,
  findingId,
}: {
  findingId?: number | string;
  className?: string;
  pointsArray: Types.Point2[];
  toolName: string;
  viewportForImage: Types.IStackViewport;
  viewportId: string;
  dicomCanvasRef: HTMLDivElement;
  color?: string;
  renderDashedLine?: boolean;
  isLocked?: boolean;
}) => {
  if (!viewportForImage) {
    return;
  }

  /**
   * In cornerstone 3D, points are expected to be in the 3d plan.
   * This reducer converts our image coordinates to world coordinates in the 3D plane.
   */
  const point3FormatPoints = pointsArray.reduce<Types.Point3[]>(
    (point3Array, point) => {
      const pointToWorldCoords = utilities.imageToWorldCoords(
        viewportForImage.getCurrentImageId(),
        point,
      );
      if (pointToWorldCoords) {
        point3Array.push(pointToWorldCoords);
      }
      return point3Array;
    },
    [],
  );

  const cornerstoneFriendlyAnnotation: Annotation = {
    annotationUID: (findingId || '') + '//' + uuidv4(),
    className,
    isVisible: true,
    invalidated: true,
    highlighted: false,
    isLocked,
    data: {
      label: '',
      cachedStats: {},
      handles: {
        textBox: {
          hasMoved: false,
          // @ts-expect-error Type 'null' is not assignable to type '{ topLeft: Point3; topRight: Point3; bottomLeft: Point3; bottomRight: Point3; }'
          worldBoundingBox: null,
          // @ts-expect-error Type 'null' is not assignable to type 'Point3'.ts(2322)
          worldPosition: null,
        },
        points: point3FormatPoints,
        activeHandleIndex: null,
      },
      isOpenContour: false,
      isOpenUShapeContour: true,
      polyline:
        toolName === PlanarFreehandROITool.toolName
          ? point3FormatPoints
          : undefined,
    },
    metadata: {
      referencedImageId: viewportForImage.getCurrentImageId(),
      volumeId: viewportId,
      toolName,
      FrameOfReferenceUID: viewportForImage.getFrameOfReferenceUID() || '',
    },
  };
  const annotationUID = annotation.state.addAnnotation(
    cornerstoneFriendlyAnnotation,
    dicomCanvasRef,
  );
  const style = {
    lineDash: renderDashedLine ? '3,2' : undefined,
    color,
    colorLocked: color,
    fill: color,
  };
  annotation.config.style.setAnnotationStyles(annotationUID, style);
};

export const useTrackImageLoadTime = () => {
  const imageLoadStartTime = useRef<{
    startTime: Date;
    isImageCached: boolean;
  }>();

  const { data } = usePreferences();
  const isCDNEnabled = !!data?.cdnEnabled;
  const estimatedVisitId = useAtomValue(selectedVisitAtom);

  const { trackEvent } = useTrackEvent();

  const timerStart = (isImageCached: boolean) => {
    imageLoadStartTime.current = { startTime: new Date(), isImageCached };
  };
  const timerEnd = (page: string, imageId: string) => {
    if (!imageLoadStartTime.current) {
      return;
    }
    trackEvent(
      mixpanelEvents.imageLoadTime,
      {
        loadTime: differenceInMilliseconds(
          new Date(),
          imageLoadStartTime.current.startTime,
        ),
        isImageCached: imageLoadStartTime.current.isImageCached,
        page,
        isCDNEnabled,
        imageId,
        estimatedVisitId,
      },
      undefined,
      undefined,
      true,
      true,
    );
  };
  return { timerStart, timerEnd };
};
